import React, { useRef, useState } from 'react'
import { Star } from 'lucide-react'

const STARS = [1, 2, 3, 4, 5] as const

interface StarRatingProps {
  /** Current score, 0 meaning unrated. */
  value: number
  /** Omit to render a read-only score. Supplying it makes the control interactive. */
  onChange?: (stars: number) => void
  size?: number
  /** Accessible name for the group. Only used in interactive mode. */
  label?: string
}

/**
 * Five stars, in two modes.
 *
 * Read-only renders one labelled image, not five controls: a score displayed beside a story title
 * is a single fact, and exposing it as five focusable elements would make every review in a list
 * five stops of tab-key noise.
 *
 * Interactive is a radiogroup with roving tabindex, which is what a "pick one of five" control is.
 * Arrow keys change the value and move focus together, so a keyboard user is never left with
 * focus on a star that is no longer the selected one.
 */
export default function StarRating({
  value,
  onChange,
  size = 20,
  label = 'Rating'
}: StarRatingProps): React.JSX.Element {
  const [hovered, setHovered] = useState(0)
  const buttons = useRef<Array<HTMLButtonElement | null>>([])

  if (!onChange) {
    return (
      <span className="star-rating" role="img" aria-label={`${value} out of 5 stars`}>
        {STARS.map((star) => (
          <Star
            key={star}
            size={size}
            aria-hidden="true"
            className={star <= value ? 'star-rating-star is-filled' : 'star-rating-star'}
          />
        ))}
      </span>
    )
  }

  const shown = hovered || value
  // Nothing selected yet still needs one stop in the tab order, so the group is reachable at all.
  const focusIndex = value >= 1 && value <= 5 ? value : 1

  const moveTo = (next: number): void => {
    if (next < 1 || next > 5) return
    onChange(next)
    buttons.current[next - 1]?.focus()
  }

  return (
    <span
      className="star-rating is-interactive"
      role="radiogroup"
      aria-label={label}
      onMouseLeave={() => setHovered(0)}
    >
      {STARS.map((star) => (
        <button
          key={star}
          ref={(node) => {
            buttons.current[star - 1] = node
          }}
          type="button"
          role="radio"
          aria-checked={value === star}
          aria-label={star === 1 ? '1 star' : `${star} stars`}
          tabIndex={star === focusIndex ? 0 : -1}
          className="star-rating-button"
          onMouseEnter={() => setHovered(star)}
          onFocus={() => setHovered(star)}
          onBlur={() => setHovered(0)}
          onClick={() => onChange(star)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
              event.preventDefault()
              moveTo(value + 1)
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
              event.preventDefault()
              moveTo(value - 1)
            }
          }}
        >
          <Star
            size={size}
            aria-hidden="true"
            className={star <= shown ? 'star-rating-star is-filled' : 'star-rating-star'}
          />
        </button>
      ))}
    </span>
  )
}
